import { FC, useEffect, useState, useMemo } from "react";
import { Room } from "../../../models/Room";
import { My } from "../../../services/My";
import { User } from "../../../models/User";
import { Fire } from "../../../services/FireClient";
import { UserAvatar } from "../../components/UserAvatar";
import InviteLinkModal from "../../components/InviteLinkModal";
import UserPopOver from "../../components/UserPopover";
import AddPeopleModal from "./AddPeopleModal";
import CallButton from '../../components/CallButton';
import { RoomCall } from '../../../models/RoomCall';
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { useToast } from "../../../components/ui/use-toast";
import { Users, ChevronUp, Crown } from 'lucide-react';
import { Badge } from "../../../components/ui/badge";
import { useMyPermissions } from '../../../context/WorkspacePermissionContext';
import { WorkspaceMemberRole } from '../../../models/Workspace';
import { IconChevronDown } from '../../components/FigmaIcons';

export const PanelListMembers: FC<{ room: Room }> = ({ room }) => {
  useLingui();
  const [isExpanded, setIsExpanded] = useState(false);
  const [call] = useState<RoomCall>();
  const [members, setMembers] = useState<User[]>(room.users);
  const { toast } = useToast();
  const perms = useMyPermissions();
  // 워크스페이스 멤버 역할 맵 (userId -> role)
  const [memberRoles, setMemberRoles] = useState<Map<string, WorkspaceMemberRole>>(new Map());

  useEffect(() => {
    setIsExpanded(false);
  }, [room.id]);

  // sync when room.users changes
  useEffect(() => {
    setMembers(room.users);
  }, [room.users]);

  // 워크스페이스 멤버 역할 로드
  useEffect(() => {
    if (!room.workspaceId) return;
    Fire.ws.getWorkspaceMembers(room.workspaceId).then(wsMembers => {
      const roles = new Map<string, WorkspaceMemberRole>();
      wsMembers.forEach(m => roles.set(m.id, m.role));
      setMemberRoles(roles);
    }).catch(() => {});
  }, [room.workspaceId]);

  // 방장을 맨 위로 정렬
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (a.id === room.creatorId) return -1;
      if (b.id === room.creatorId) return 1;
      return 0;
    });
  }, [members, room.creatorId]);

  function isCallOngoing() {
    return RoomCall.isActive(call);
  }

  const canKickMembers = !!Room.isCreator(room, My.user.id);

  async function handleKickMember(member: User) {
    try {
      setMembers((prev: User[]) => prev.filter((m: User) => m.id !== member.id));
      await Fire.removeRoomMember(room.id, member, My.user);
      toast({
        title: t`${member.name} has been kicked out`,
        
      });
    } catch (e) {
      setMembers(room.users); // rollback
      toast({
        title: t`Failed to kick out`,
        duration: 1500,
      });
    }
  }

  return (
    <div className="w-full">
      {/* Figma: 6px 회색 구분선 */}
      <div className="h-1.5 bg-muted w-full" />

      {/* 섹션 본체 */}
      <div className="border-b border-border px-4 py-4">
        {/* 헤더 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-heading-sm text-[color:rgb(var(--text-section))] hover:opacity-70 transition-opacity"
            >
              <span className="font-bold"><Trans>Members</Trans></span>
              <span className="font-normal">({room.users.length})</span>
              <span className="ml-0.5">
                {isExpanded ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <IconChevronDown className="w-3.5 h-3.5" />
                )}
              </span>
            </button>

            {!Room.isArchivedOneToOne(room) && (
              <CallButton room={room} isOngoing={isCallOngoing()} call={call} />
            )}
          </div>

          {!room.isOneToOne && (
            <div className="flex items-center gap-1">
              {room.workspaceId && Room.isCreator(room, My.user.id) && <AddPeopleModal room={room} />}
              {perms?.canCreateRoomInviteLink === true && <InviteLinkModal room={room} />}
            </div>
          )}
        </div>

        {/* 구분선 - 점선 (Figma: stroke-dasharray 2 2, #E3E6EA) */}
        <div className="-mx-4 mt-3.5 border-b border-dashed border-border" />

        {/* 바디 */}
        <div className="mt-3.5">
          {/* 접힌 상태: 그리드로 아바타만 표시 */}
          {!isExpanded && (
            <div className="flex flex-wrap gap-2.5 animate-in slide-in-from-top-1 duration-200">
              {sortedMembers.slice(0, 10).filter(u => !!u).map((u) => (
                <UserPopOver
                  user={u}
                  key={u.id}
                  room={room}
                  isCreator={u.id === room.creatorId}
                  isGuest={memberRoles.get(u.id) === 'guest'}
                  canKick={canKickMembers}
                  onKick={handleKickMember}
                  content={
                    <div className="relative w-10 h-10 cursor-pointer hover:opacity-80 transition-opacity">
                      <UserAvatar
                        src={u.photoUrl}
                        name={u.name}
                        className="w-full h-full"
                      />
                      {u.id === room.creatorId && (
                        <div className="absolute -bottom-0.5 -right-0.5 bg-primary rounded-full p-0.5 z-10 border border-transparent">
                          <Crown className="w-2.5 h-2.5 text-on-primary-text" />
                        </div>
                      )}
                    </div>
                  }
                />
              ))}
            {sortedMembers.length > 10 && (
              <button
                onClick={() => setIsExpanded(true)}
                className="w-10 h-10 rounded-[rgb(var(--radius-avatar))] bg-muted flex items-center justify-center text-xs text-muted-foreground hover:bg-accent transition-colors"
              >
                +{sortedMembers.length - 10}
              </button>
            )}
          </div>
        )}

        {/* 펼친 상태: 리스트로 상세 정보 표시 */}
        {isExpanded && (
          <div className="flex flex-col -mx-2 animate-in slide-in-from-top-1 duration-200">
            {sortedMembers.filter(u => !!u).map((u) => (
              <UserPopOver
                user={u}
                key={u.id}
                room={room}
                isCreator={u.id === room.creatorId}
                isGuest={memberRoles.get(u.id) === 'guest'}
                canKick={canKickMembers}
                onKick={handleKickMember}
                content={
                  <div className="flex items-center gap-3 py-2.5 px-2 hover:bg-accent rounded-lg transition-colors cursor-pointer">
                    <div className="relative w-10 h-10 flex-shrink-0">
                      <UserAvatar
                        src={u.photoUrl}
                        name={u.name}
                        className="w-full h-full"
                      />
                      {u.id === room.creatorId && (
                        <div className="absolute -bottom-0.5 -right-0.5 bg-primary rounded-full p-0.5 z-10 border border-transparent">
                          <Crown className="w-3 h-3 text-on-primary-text" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm truncate text-[color:rgb(var(--text-body))]">{u.name}</h3>
                        {u.id === room.creatorId && (
                          <Badge className="bg-primary text-on-primary-text hover:bg-primary text-meta-sm px-1.5 py-0 gap-0.5">
                            <Crown className="w-2.5 h-2.5" />
                            <Trans>Owner</Trans>
                          </Badge>
                        )}
                        {memberRoles.get(u.id) === 'guest' && (
                          <Badge className="bg-muted text-muted-foreground hover:bg-muted text-meta-sm px-1.5 py-0">
                            <Trans>Guest</Trans>
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                }
              />
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
